import autobind from 'core-decorators/lib/autobind';
import { notForPreview } from 'widget/utils/not-for-preview';
import { connect } from 'react-redux';

import { VODCSSModules, VODBiEvents } from 'shared/utils/wix-connect';
import { getNewRelic } from 'shared/analytics/new-relic';
import {
  getAllSettings,
  getSiteUrl,
  getRequestedWidgetSize,
  isPreviewHover,
  isVideoListPreviewHover,
  canShowActionBar,
  canShowChannelShare,
  canShowVideoList,
  canShowSignIn,
  canShowChannelInfo,
  isCompactPlayInFrame,
} from 'shared/selectors/app-settings';

import { isVisible as canShowSubscriptionButton } from 'widget/components/overlay-texts/channel/subscribe-button-text';

import {
  openFullScreenOverlay,
  openFullScreenMemberOnlyOverlay,
  openFullScreenSubscribeOverlay,
  openFullScreenPurchaseOverlay,
} from 'shared/utils/open-overlay';
import { isMobile, isTablet } from 'shared/utils/device/screen';
import { playerSize } from 'widget/utils/videos-sizes';

import { getMainVideoId } from 'widget/ui-selectors/selected-video-id';
import { isVideoPlaying, isVideoPaused } from 'widget/selectors/video-playback-status';
import { getVideosGroupedByIds } from 'shared/selectors/videos';

import { DATA_KEY } from 'widget/data-components/paginated-channel-videos';
import { COMPACT_VIDEOS_IN_ROW, COMPACT_NUMBER_OF_ROWS } from 'shared/constants/compact-layout';

import BaseView from 'widget/views/_base';

import UpdateWidgetSize from 'widget/data-components/update-widget-size';
import PaymentEvents from 'shared/components/payment-events/payment-events';
import ReloadStorage from 'widget/components/reload-storage/reload-storage';
import ResizeWidgetByContent from 'widget/views/desktop/compact/components/resize-widget-by-content/resize-widget-by-content';

import IconButton from 'shared/components/button/icon/icon';
import Player from 'widget/views/desktop/compact/components/player/player';
import Videos from 'widget/views/desktop/compact/components/videos/videos';
import ClosableOverlay from 'widget/views/desktop/compact/components/closable-overlay/closable-overlay';
import ShareChannel from 'widget/views/desktop/compact/components/share-channel/share-channel';
import { ChannelOverlay, VideoOverlay } from 'widget/views/desktop/compact/components/player-overlay/overlay';
import ChanelActions from 'widget/views/desktop/compact/components/channel-actions/channel-actions';
import ActionBar from 'widget/views/desktop/compact/components/action-bar/action-bar';

import { playVideo } from 'widget/redux/client/actions/change-playback-status';
import { selectVideo } from 'widget/redux/client/actions/select-video';

import InFrameActions from 'widget/views/desktop/compact/actions/overlay-actions/in-frame';
import InFrameMobileActions from 'widget/views/desktop/compact/actions/overlay-actions/in-frame-mobile';
import InOverlayActions from 'widget/views/desktop/compact/actions/overlay-actions/in-overlay';

import Animate from 'widget/views/desktop/compact/components/animate/animate';

import styles from './compact.styl';


const DEFAULT_STATE = {
  canShowChannelCover: true,
  areAllVideosVisible: false,
  isShareOverlayVisible: false,
  isInFullScreen: false,
};

const IS_MOBILE_DEVICE = isMobile() || isTablet();
const MIN_WIDGET_WIDTH = 400;

const ACTION_BAR_OPENED_HEIGHT = 60;
const ACTION_BAR_CLOSED_HEIGHT = 40;


function getOverlayActionsWrapper(store) {
  if (IS_MOBILE_DEVICE) {
    return InFrameMobileActions;
  }

  if (isCompactPlayInFrame(store)) {
    return InFrameActions;
  }

  return InOverlayActions;
}


@connect((store, ownProps) => ({
  isVideoPaused: isVideoPaused(store),
  isVideoPlaying: isVideoPlaying(store),
  mainVideoId: getMainVideoId(store, DATA_KEY),

  channelVideoIds: ownProps.paginatedChannelVideos.ids,
  prevPageIds: ownProps.paginatedChannelVideos.prevPageIds,
  nextPageIds: ownProps.paginatedChannelVideos.nextPageIds,
  prevVideoId: ownProps.nextChannelVideos.prevId,
  nextVideoId: ownProps.nextChannelVideos.nextId,
  goBack: ownProps.paginatedChannelVideos.goBack,
  goForward: ownProps.paginatedChannelVideos.goForward,

  appSettings: getAllSettings(store),
  siteUrl: getSiteUrl(store),
  videoByIds: getVideosGroupedByIds(store),
  requestedWidgetSize: getRequestedWidgetSize(store),

  canShowChannelCover: !store.isVideosTouched,
  canShowActionBar: canShowActionBar(store),
  canShowChannelInfo: canShowChannelInfo(store),
  canShowChannelShare: canShowChannelShare(store),
  canShowVideoList: canShowVideoList(store),
  canShowSignIn: canShowSignIn(store),
  canShowSubscriptionButton: canShowSubscriptionButton({ channel: ownProps.channelData }),
  isPreviewHover: isPreviewHover(store),
  isVideoListPreviewHover: isVideoListPreviewHover(store),

  OverlayActions: getOverlayActionsWrapper(store),
}), {
  playVideo,
  selectVideo,
})
@VODBiEvents()
@VODCSSModules(styles)
export default class CompactView extends BaseView {
  static displayName = 'CompactView';

  static propTypes = {
    mainVideoId: React.PropTypes.string,
    channelVideoIds: React.PropTypes.arrayOf(React.PropTypes.string),
    prevPageIds: React.PropTypes.arrayOf(React.PropTypes.string),
    nextPageIds: React.PropTypes.arrayOf(React.PropTypes.string),

    canShowActionBar: React.PropTypes.bool,
    canShowChannelShare: React.PropTypes.bool,
    canShowChannelInfo: React.PropTypes.bool,
    canShowVideoList: React.PropTypes.bool,
    canShowSignIn: React.PropTypes.bool,
    isPreviewHover: React.PropTypes.bool,
    isVideoListPreviewHover: React.PropTypes.bool,
  };

  constructor(props) {
    super(props);

    this.state = DEFAULT_STATE;

    getNewRelic().setCustomAttribute('layout', 'compact');
  }

  @autobind
  reset() {
    // TODO: Base tries to call resetUI from redux-ui, need to inverstigate if it does anything useful
    //super.reset();
    this.mainVideoList.toFirstPage();
  }

  @autobind
  showAllVideos() {
    this.setState({
      areAllVideosVisible: true,
    });
  }

  @autobind
  hideAllVideos() {
    this.setState({
      areAllVideosVisible: false,
    });
  }

  @autobind
  toggleOverlay() {
    const { isShareOverlayVisible } = this.state;
    this.setState({
      isShareOverlayVisible: !isShareOverlayVisible,
    });
  }

  @autobind
  openMembership() {
    openFullScreenMemberOnlyOverlay(this.props.channelData.id, this.mainVideo.id);
  }

  get shareButtons() {
    const { appSettings, channelData } = this.props;
    const { isShareOverlayVisible } = this.state;

    if (!isShareOverlayVisible) {
      return null;
    }

    return (
      <ShareChannel channelData={channelData}
                    appSettings={appSettings} />
    );
  }

  get shareChannelOverlay() {
    const { canShowChannelShare } = this.props;
    const { isShareOverlayVisible } = this.state;

    if (!canShowChannelShare) {
      return null;
    }

    return (
      <Animate styleName="share-channel-overlay"
               type="fade-in-out"
               when={isShareOverlayVisible}>
        <ClosableOverlay onClose={this.toggleOverlay}>
          {this.shareButtons}
        </ClosableOverlay>
      </Animate>
    );
  }

  get prevVideoButton() {
    const { prevVideoId } = this.props;

    if (!prevVideoId) {
      return null;
    }

    return (
      <IconButton className={'qa-prev-button'}
                  styleName="button prev-button"
                  onClick={this.selectPrevVideo}
                  name="arrow-strip" />
    );
  }

  get nextVideoButton() {
    const { nextVideoId } = this.props;

    if (!nextVideoId) {
      return null;
    }

    return (
      <IconButton className={'qa-next-button'}
                  styleName="button next-button"
                  onClick={this.selectNextVideo}
                  name="arrow-strip" />
    );
  }

  get playerOverlay() {
    const mainVideo = this.mainVideo;

    if (!mainVideo) {
      // TODO Move to component
      // each initial state need to be a single component
      return this.noVideosPlayerContent;
    }

    const { appSettings, channelData, OverlayActions } = this.props;

    const Overlay = this.isChannelCoverShown ? ChannelOverlay : VideoOverlay;

    return (
      <div styleName="player-container">
        {this.prevVideoButton}
        {this.nextVideoButton}
        <OverlayActions isMainVideo
                        channelId={channelData.id}
                        videoId={mainVideo.id}>
          <Overlay styleName="player-overlay"
                   channelData={channelData}
                   appSettings={appSettings}
                   videoItem={mainVideo} />
        </OverlayActions>
      </div>
    );
  }

  get player() {
    const mainVideo = this.mainVideo;
    const { channelData } = this.props;

    // TODO wrap with container to provide playback actions like onPlayRequested
    // wrapper, not pure actions needed because of different logic for views
    // Also can be done as view specific actions (need to discuss)
    return (
      <Player video={mainVideo}
              channel={channelData}
              onFullScreenChanged={this.setFullScreenState} />
    );
  }

  get playerContentSize() {
    const { width, height } = this.playerSize;
    return {
      width,
      height: height - (this.actionBarHeight - ACTION_BAR_CLOSED_HEIGHT),
    };
  }

  get playerSize() {
    const { requestedWidgetSize } = this.props;
    const width = Math.max(requestedWidgetSize.width, MIN_WIDGET_WIDTH);
    return playerSize(width, 0);
  }

  get actionBarHeight() {
    return this.areActionsVisible ? ACTION_BAR_OPENED_HEIGHT : ACTION_BAR_CLOSED_HEIGHT;
  }

  get thumbnailSize() {
    const { width, height } = this.playerSize;

    return {
      width: width / COMPACT_VIDEOS_IN_ROW,
      height: (height - (ACTION_BAR_OPENED_HEIGHT - ACTION_BAR_CLOSED_HEIGHT)) / COMPACT_NUMBER_OF_ROWS,
    };
  }

  get videos() {
    const {
      channelData,
      channelVideoIds,
      prevPageIds,
      nextPageIds,
      goBack,
      goForward,
      canShowVideoList,
    } = this.props;

    if (!canShowVideoList) {
      return null;
    }

    return (
      <Videos channelData={channelData}
              channelVideoIds={channelVideoIds}
              prevPageIds={prevPageIds}
              nextPageIds={nextPageIds}
              onSlideToPrev={goBack}
              onSlideToNext={goForward}
              onPlayClick={this.playVideo}
              onThumbnailClick={this.handleThumbnailClicked}
              thumbnailSize={this.thumbnailSize} />
    );
  }

  @autobind
  handleThumbnailClicked(video) {
    if (video.id !== this.props.mainVideoId || this.canShowChannelCover) {
      //this.sendVidSelectedBiEvent(video);
    }

    this.selectVideo(video);
  }

  @autobind
  playVideo(videoData) {
    const { currentSiteUser, channelData, mainVideoId } = this.props;
    const { id, memberOnly } = videoData;

    if (IS_MOBILE_DEVICE) {
      // we can`t start playing for not rendered player because of browser restriction
      if (id === mainVideoId) {
        return super.playVideo(videoData);
      }
      return this.handleThumbnailClicked(videoData);
    }

    if (!memberOnly || memberOnly && currentSiteUser.data) {
      openFullScreenOverlay(`#/channel/${channelData.id}/video/${id}/play`);
    } else {
      openFullScreenMemberOnlyOverlay(channelData.id, id);
    }
  }

  @autobind
  @notForPreview
  openSubscription() {
    const { channelData, biEvent } = this.props;

    openFullScreenSubscribeOverlay(channelData.id, () => {
      biEvent('widget.subscriptionPopUp.Completed', { result: 'canceled', errorMsg: '' });
    });
  }

  get actionBar() {
    const { canShowActionBar } = this.props;

    if (!canShowActionBar) {
      return null;
    }

    return (
      <ActionBar height={this.actionBarHeight}
                 areActionsVisible={this.areActionsVisible}
                 actions={this.actions}
                 onMenuClick={this.showAllVideos} />
    );
  }

  @autobind
  @notForPreview
  openPurchase() {
    const { channelData, videoItem } = this.props;

    openFullScreenPurchaseOverlay(channelData.id, videoItem.id);
  }

  @autobind
  showChannelInfo(event) {
    event.preventDefault();

    openFullScreenOverlay(`#/channel/${this.props.channelData.id}/info`);
  }

  get actions() {
    const {
      channelData,
      canShowVideoList,
      canShowSignIn,
      canShowChannelShare,
      canShowChannelInfo,
      canShowSubscriptionButton,
    } = this.props;

    return (
      // TODO wrap with container to provide playback actions like onShareRequested
      // wrapper, not pure actions needed because of different logic for views
      // Also can be done as view specific actions (need to discuss)

      // TODO onUserLogin, onUserLogOut - etc. make BaseAccountButton dumb to only
      // call callbacks provided to it aka onUserLoginRequested...
      <ChanelActions channelData={channelData}
                     canShowSignIn={canShowSignIn}
                     canShowChannelShare={canShowChannelShare}
                     canShowChannelInfo={canShowChannelInfo}
                     canShowCloseButton={canShowVideoList}
                     canShowSubscriptionButton={canShowSubscriptionButton}
                     onShareRequested={this.toggleOverlay}
                     onChannelInfoRequested={this.showChannelInfo}
                     onSubscriptionRequested={this.openSubscription}
                     onUserLogin={_.noop}
                     onUserLogOut={_.noop}
                     onSubscribeClick={_.noop}
                     onSubscriptionCancelClick={_.noop}
                     onClose={this.hideAllVideos} />
    );
  }

  get isVideoListVisible() {
    const { areAllVideosVisible } = this.state;
    const { isVideoListPreviewHover, isPreviewHover, canShowVideoList } = this.props;
    if (!canShowVideoList) {
      return false;
    }
    return areAllVideosVisible || isVideoListPreviewHover || isPreviewHover;
  }

  get areActionsVisible() {
    const { canShowVideoList } = this.props;
    return !canShowVideoList || this.isVideoListVisible;
  }

  get isVideosLoaded() {
    const { videoByIds, paginatedChannelVideos } = this.props;
    return !paginatedChannelVideos.isFetching || !_.isEmpty(videoByIds);
  }

  render() {
    if (!this.isUIBlockingDataLoaded || !this.isVideosLoaded) {
      return this.spinner;
    }

    const { isVideoPlaying, isVideoPaused } = this.props;

    return (
      <ResizeWidgetByContent>
        <section styleName="container"
                 ref={this.setContainerEl}
                 className="qa-widget-container">
          <UpdateWidgetSize disabled={this.state.isInFullScreen} />
          <ReloadStorage onReload={this.processDataFromStorage} />

          <div styleName="content"
               style={this.playerContentSize}>
            <Animate type="fade-in"
                     styleName="videos-in-animation"
                     when={this.isVideoListVisible}>
              {this.videos}
            </Animate>
            <Animate type="fade-in"
                     when={!this.isVideoListVisible}>
              {(isVideoPlaying || isVideoPaused) ? this.player : this.playerOverlay}
            </Animate>
            {this.shareChannelOverlay}
          </div>

          {this.actionBar}

          <PaymentEvents onRent={this.setCurrentVideoFromPayment}
                         onSale={this.setCurrentVideoFromPayment}
                         onSubscription={this.reset}
                         onSubscriptionCancel={this.reset} />
        </section>
      </ResizeWidgetByContent>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/views/desktop/compact/compact.js